import * as T from './types';
import TenantStrategy from './TenantStrategy';
import { AuthContextEnum } from '../../../services/authTokenService';
import AccountsClient from '../../../clients/stratus/accountmgtsvc/AccountsClient';
import { ISessionService } from '../../../services/session';
import TenantUtils from '../TenantUtils';
import StratusTenantHandler from './StratusTenantHandler';
import { TenantStrategyEnum } from './strategy';
import { IAuthProviderService } from '../../../services/authProviderService';
import UserMgtClient from '../../../clients/stratus/usersMgt/UserMgtClient';
import { ResourceListType } from '../../../clients/stratus/tntCommonResourceRegistry/types';
import UserTenantDetailsClient from '../../../clients/stratus/usersMgt/UserTenantDetailsClient';
import {
  DEFAULT_COUNTRY,
  DEFAULT_LANGUAGE,
  DEFAULT_PAGINATION
} from './constants';
import TenantFilterBuilder from '../tenantFilterBuilder/TenantFilterBuilder';
import { DEFAULT_PAGINATION_SIZE_GET_NODE_PERSON_DOMAIN } from '../../../clients/stratus/tntCommonResourceRegistry/constants';

type OrganizationProperties = {
  fallback?: string;
  filter?: {
    role: 'admin' | 'user' | 'all';
    tenantType: string[];
  };
  customBehaviors?: [
    {
      filter?: {
        role: 'admin' | 'user' | 'all';
        tenantType: string[];
      };
      onSelect: {
        redirectTo?: string;
      };
    }
  ];
};

export default class OrganizationTenantHandler
  extends StratusTenantHandler
  implements TenantStrategy
{
  private _strategy = TenantStrategyEnum.stratusOrganization;
  private _options: OrganizationProperties;
  private _assetReference: string;
  private _userTenantDetailsClient: UserTenantDetailsClient;
  private _accountsClient: AccountsClient;
  private _sessionService: ISessionService;
  private _userMgtClient: UserMgtClient;
  private _isNative: boolean;

  constructor({
    stack,
    options,
    parentAuthProvider,
    authProvider,
    assetReference,
    sessionService,
    isNative,
    tenantsDataRepository,
    eventService
  }: T.TenantHandlerStrategyType) {
    super({
      stack,
      authProvider,
      parentAuthProvider,
      tenantsDataRepository,
      eventService
    });
    this._options = options;
    this._assetReference = assetReference;
    this._sessionService = sessionService;
    this._isNative = isNative;
    this._accountsClient = new AccountsClient(
      AccountsClient.getBaseUrl(this._stack),
      this._parentAuthProvider
    );
    this._userMgtClient = new UserMgtClient(
      UserMgtClient.getBaseUrl(stack),
      this._authProvider
    );
    this._userTenantDetailsClient = new UserTenantDetailsClient(
      UserTenantDetailsClient.getBaseUrl(this._stack),
      this._parentAuthProvider
    );
  }

  public getAssetReference(): string {
    return this._assetReference;
  }

  public getStrategy = (): TenantStrategyEnum => {
    return this._strategy;
  };

  public setTenant = async (
    id: string,
    authContext: AuthContextEnum
  ): Promise<void> => {
    await this._sessionService.exchangeToken(
      id,
      authContext,
      this._isNative
        ? TenantStrategyEnum.stratusNative
        : TenantStrategyEnum.stratusOrganization
    );
  };

  /**
   * Creates a new tenant/organization with the provided options
   * @param options - The tenant creation options
   * @returns Promise resolving to the created tenant result with resourceId
   */
  public async createTenant(
    options: T.CreateTenantStrategyOptionsType
  ): Promise<T.CreateTenantResultType> {
    const response = await this._accountsClient?.createAccount({
      accountId: undefined,
      accountName: options?.accountName,
      language: options?.language || DEFAULT_LANGUAGE,
      countrySet: options?.countrySet || [DEFAULT_COUNTRY],
      idToken: options.idToken,
      type: options.type,
      parentAccountResourceId: options?.parentAccountResourceId
    });

    return { resourceId: response.resourceId };
  }

  /**
   * Fetches paginated tenant list data from the backend services
   * @param options - Options for pagination and filtering
   * @returns Promise resolving to paginated tenant visualization data
   */
  public async _getPaginatedTenantListData(
    options: T.GetTenantListOptionsType
  ): Promise<T.PaginatedTenantVisualizationType> {
    const { filter } = this._options || {};
    const userResourceId = await this._userMgtClient
      .getUsersMe()
      .then((response) => response.resourceId);

    const _options = {
      userResourceId,
      ...options
    };
    const customBehaviorsList = this._options?.customBehaviors;

    const userTenantDetails =
      await this._userTenantDetailsClient.getUserTenantDetails(_options);

    const cachedTenants =
      this._tenantsDataRepository.findOne(this.TENANT_DATA_REPOSITORY_SUFFIX) ||
      {};

    const totalPages = Math.ceil(
      userTenantDetails.totalCount /
        DEFAULT_PAGINATION_SIZE_GET_NODE_PERSON_DOMAIN
    );
    const tenantsInCache: T.TenantVisualizationType[] = [];

    const tenantList = await Promise.all<T.TenantNodeType>(
      userTenantDetails?.resourceList?.map(
        async (resource: ResourceListType) => {
          const tenantDataCached = cachedTenants?.[resource.tenantResourceId];
          const shouldReuseCachedTenant = !options?.refresh && tenantDataCached;

          // Use the cached tenant data if available
          if (shouldReuseCachedTenant) {
            tenantsInCache.push(tenantDataCached);
            return;
          }

          // Fetch the tenant data if not in cache
          const tenant = await this._nodeClient.getNodeByTenantId({
            tenantId: resource.tenantResourceId
          });
          return { ...tenant, roleCategory: resource.roleCategory };
        }
      )
    );

    const validFetchedTenants = tenantList
      .filter((tenant) => tenant !== undefined) // Remove the null in the array caused by the cached tenants in the previous step
      .map((tenant) => {
        return TenantUtils._convertStratusTenantNodeToTenantVisualizationType(
          tenant
        );
      });

    const tenantListWithFetchedAndCachedTenantData = [
      ...validFetchedTenants,
      ...tenantsInCache
    ];

    const filteredTenantList = this.getFilteredTenantList(
      filter,
      tenantListWithFetchedAndCachedTenantData
    );

    let unifiedTenantList = filteredTenantList;

    if (customBehaviorsList) {
      unifiedTenantList = this.addCustomBehaviorsTenantList(
        customBehaviorsList,
        unifiedTenantList,
        tenantListWithFetchedAndCachedTenantData
      );
      return {
        tenants: unifiedTenantList,
        currentPage: _options.page || DEFAULT_PAGINATION.CURRENT_PAGE,
        totalPages: totalPages || DEFAULT_PAGINATION.TOTAL_PAGES
      };
    } else {
      return {
        tenants: filteredTenantList,
        currentPage: _options.page || DEFAULT_PAGINATION.CURRENT_PAGE,
        totalPages: totalPages || DEFAULT_PAGINATION.TOTAL_PAGES
      };
    }
  }

  private getFilteredTenantList(
    filter: T.TenantHandlerFilterType,
    tenants: T.TenantVisualizationType[]
  ) {
    return new TenantFilterBuilder({
      filter,
      tenants
    })
      .filterByRegexTenantType()
      .filterByRole()
      .getTenantList();
  }

  public getAuthProvider(): IAuthProviderService {
    return this._authProvider.createOrgedAuthProvider();
  }

  public isRequiredToNavigate = (): boolean => {
    return true;
  };

  public getFallback = (): string => {
    return this?._options?.fallback;
  };

  /**
   * Validates if a tenant is valid based on the provided filter
   * @param filter - Filter criteria to validate against
   * @param tenant - Tenant to validate
   * @returns Boolean indicating if tenant is valid
   */
  public isTenantValid(
    filter: T.TenantHandlerFilterType,
    tenant: T.TenantVisualizationType
  ): boolean {
    return this.getFilteredTenantList(filter, [tenant])?.length > 0;
  }

  /**
   * Adds custom behaviors to the tenant list based on configuration
   * @param customBehaviorsList - List of custom behaviors to apply
   * @param filteredTenantsList - Already filtered tenant list
   * @param allTenantsList - Complete list of tenants
   * @returns Merged and deduplicated tenant list
   */

  private addCustomBehaviorsTenantList(
    customBehaviorsList: OrganizationProperties['customBehaviors'],
    filteredTenantsList: T.TenantVisualizationType[],
    allTenantsList: T.TenantVisualizationType[]
  ): T.TenantVisualizationType[] {
    if (!customBehaviorsList || !filteredTenantsList || !allTenantsList) {
      return filteredTenantsList;
    }

    let resultList = filteredTenantsList;

    for (const customBehaviors of customBehaviorsList) {
      const filterCustomBehavior = customBehaviors.filter;

      // Grant filters can't be resolved synchronously here; they're only
      // checked at select-time (TenantHandlerService), so skip them to
      // avoid matching every tenant when tenantType/role aren't also set.
      if ((filterCustomBehavior as T.TenantHandlerFilterType)?.grant) {
        continue;
      }

      const filteredCustomBehaviors = this.getFilteredTenantList(
        filterCustomBehavior,
        allTenantsList
      );

      resultList = super.mergeListsAndRemoveDuplicates(
        resultList,
        filteredCustomBehaviors
      );
    }

    return resultList;
  }
}
